import { Component, inject, Input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Meldung } from '@app/shared/classes/meldung';
import { MeldungBewilligung } from '@app/shared/classes/meldung-bewilligung';
import { BackendService } from '@app/shared/services/backend.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';

import type { Sprache } from '@app/shared/sprache';

@Component({
  selector: 'meldung-bewilligen-dialog',
  templateUrl: './meldung-bewilligen-dialog.component.html',
  imports: [
    FormsModule,
    TranslatePipe,
  ]
})
export class MeldungBewilligenDialogComponent {
  private readonly backendService = inject(BackendService);
  private readonly translateService = inject(TranslateService);

  private id : number|null = null;

  protected readonly tierarztSprache = signal<string|null>(null);
  protected readonly sprache = signal<Sprache>('DE');
  protected readonly kommentar = signal('');

  protected readonly requestPending = signal<boolean>(false);
  protected readonly preview = signal<string[]>([]);
  protected readonly page = signal<number>(0);

  readonly result = output<Meldung>();

  @Input()
  set meldung(value: Meldung | null) {
    this.id = value?.id ?? null;
    if (value) {
      this.backendService.letzteSprache(value.id)
        .subscribe({
          next: sprache => {
            if (sprache) {
              this.sprache.set(sprache);
              this.tierarztSprache.set(
                this.translateService.instant('meldung.view.bewilligen.sprache', {
                  sprache: this.translateService.instant('global.sprache.' + sprache.toLowerCase())
                })
              );
            } else {
              this.tierarztSprache.set(null);
            }
          },
          error: console.error
        });
    }
  }

  protected cancel(): void {
    Swal.close();
  }

  protected openPreview(): void {
    this.requestPending.set(true);
    this.backendService.bewilligeMeldungPreview(this.bewilligungRequest)
      .subscribe({
        next: result => {
          this.page.set(0);
          this.preview.set(result.pages);
          this.requestPending.set(false);
        },
        error: error => {
          console.error(error);
          this.requestPending.set(false);
        }
      });
  }

  private get bewilligungRequest(): MeldungBewilligung {
    if (!this.id) {
      throw new Error('Meldung not initialized');
    }
    return {
      id        : this.id,
      sprache   : this.sprache(),
      kommentar : this.kommentar()
    };
  }

  protected save(): void {
    this.requestPending.set(true);
    this.backendService.bewilligeMeldung(this.bewilligungRequest)
      .subscribe( meldung => {
        Swal.close();
        this.result.emit(meldung)
      });
  }

  protected changePage(diff: number): void {
    this.page.update(page => page + diff);
  }
}
